import React, { useState, useEffect } from "react";
import { Tooltip, IconButton, TablePagination } from "@mui/material";
import { useData } from "@pure-ptr/react";
import makeStyles from "@mui/styles/makeStyles";
import Helmet from "react-helmet";

import {
    Portlet,
    PortletBody,
    PortletHeader,
    PortletHeaderToolbar,
} from "../../../partials/content/Portlet";
import { DataTable, DataFilter, TablePaginationActions } from "../../../common";
import EditIcon from "@mui/icons-material/Edit";
import PartnerIPMaskDialog from "./PartnerIpMaskDialog/PartnerIpMaskDialog";
import { adjustCurrentPage } from "../../../util";
import {
    createStringDataFieldwithEllipis,
    createIconDataField,
    createStringDataField,
} from "../../../util/format";
import { StringEquators } from "../../../store/constants/pages";
import { nonPaginatedSorting } from "../../../util";
import { filterData } from "../../../util/selector";
import { getPartnerIpMasks } from "../../../store/api/partnerIpmask";

const useStyles = makeStyles((theme) => ({
    root: {
        margin: "auto",
    },
    button: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(1),
    },
    icon: {
        fontSize: 17,
        marginTop: 1,
    },
    textField: {
        width: 400,
    },
}));

const fields = [
    createStringDataField("partnerName", "Partner Name", {
        sortable: true,
        width: 200,
        equators: StringEquators,
        whiteSpace: "break-spaces",
    }),

    createStringDataFieldwithEllipis("ipMask", "IP CIDR", {
        width: 800,
        wordBreak: "break-word",
        returnWholeRow: true,
        ellipisisWidth:100,
    }),
    createStringDataFieldwithEllipis("description", "Description", {
        width: 400,
        wordBreak: "break-word",
        returnWholeRow: true,
        ellipisisWidth:50,
    }),
    createIconDataField("active", "Active", {
        hideFromFilter: true,
        icon: "fas fa-check-circle",
        sortable: true,
    }),
];


export default function PartnerIpMaskPage() {

        const {
            data,
            isPending,
            error,
            reload
        } = useData(async () => getPartnerIpMasks(), []);

    const [isEditOpen, setEditOpen] = useState(false);
    const [sort, setSort] = useState([]);
    const [filter, setFilter] = useState('');
    const [selectedCidr, setSelectedCidr] = useState(null);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [page, setPage] = useState(0);

    const classes = useStyles();

    useEffect(() => {
        let currentPage = page + 1;
        let totalRecords = data?.length;
        let pageSize = rowsPerPage;

        currentPage = adjustCurrentPage(currentPage, totalRecords, pageSize);
        if (page === currentPage - 1) return;
        setPage(currentPage - 1);
    }, [data]);


    const handleRefresh = () => {
        reload()
    };

    const handleSearchInput = (value) => {
        setFilter(value);
    };

    const handleEditClose = (shouldReload) => {
        setEditOpen(false);
        setSelectedCidr(null);
        if (shouldReload) {
            handleRefresh();
        }
    };

    const handleAddIpMask = () => {
        setEditOpen(true);
    };

    const handleEditCidr = (rowData) => {
        setEditOpen(true);
        setSelectedCidr(rowData);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const sorted = nonPaginatedSorting(
        sort,
        (data || [])?.slice(
            page * rowsPerPage,
            page * rowsPerPage + rowsPerPage
        )
    )?.tableData;

    const filteredData=filterData(fields?.map((item)=>item?.id),filter,sorted)

    function partnerIpMaskAction(rowData) {
        return (
            <div className="d-flex">
                <IconButton
                    color="primary"
                    className="p-1"
                    aria-label="Edit"
                    onClick={() => handleEditCidr(rowData)}
                    size="large"
                >
                    <Tooltip title="Edit">
                        <EditIcon />
                    </Tooltip>
                </IconButton>
            </div>
        );
    }

    return (
        <>
            <Helmet title="Partner IP Mask" />
            <Portlet>
                <PortletHeader
                    title="Partner IP Mask"
                    handleRefresh={handleRefresh}
                    name="partners-ip-mask"
                    toolbar={
                        <PortletHeaderToolbar>
                            <DataFilter
                                onChange={handleSearchInput}
                                placeholder="Search"
                                cssClass={classes.textField}
                                filter={filter}
                            />
                        </PortletHeaderToolbar>
                    }
                />
                <PortletBody>
                    <div className="row">
                        <div className="col-md-12">
                            <DataTable
                                name="Partner IP Mask"
                                fields={fields}
                                data={filteredData}
                                handleRefresh={handleRefresh}
                                onAdd={handleAddIpMask}
                                title="Partner IP Mask"
                                loading={isPending!=null}
                                sort={sort}
                                onSort={setSort}
                                stickyHeader={true}
                                showPagination={true}
                                footerPagination={() => (
                                    <TablePagination
                                        rowsPerPageOptions={[
                                            10, 50, 100, 250, 500,
                                        ]}
                                        colSpan={fields.length + 2}
                                        count={data?.length}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        SelectProps={{
                                            inputProps: {
                                                "aria-label": "rows per page",
                                            },
                                            native: true,
                                        }}
                                        onPageChange={handleChangePage}
                                        onRowsPerPageChange={
                                            handleChangeRowsPerPage
                                        }
                                        ActionsComponent={
                                            TablePaginationActions
                                        }
                                    />
                                )}
                                hasActions={true}
                                renderActionColumn={partnerIpMaskAction}
                            />
                        </div>
                    </div>
                </PortletBody>
            </Portlet>
            <PartnerIPMaskDialog
                data={data}
                open={isEditOpen}
                handleClose={handleEditClose}
                cidr={selectedCidr}
            />
        </>
    );
}
